import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bus, Clock, CurrencyInr, Lightning, NavigationArrow, MapPin, Users,
  ArrowRight, Path, MapTrifold, ArrowDown, CheckCircle, Hourglass, SealCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";

export default function BusResults() {
  const [params] = useSearchParams();
  const pickup = params.get("pickup") || "";
  const drop = params.get("drop") || "";
  const [loading, setLoading] = useState(true);
  const [buses, setBuses] = useState([]);
  const [multiLeg, setMultiLeg] = useState([]);
  const [selected, setSelected] = useState(null);
  const [passengers, setPassengers] = useState(1);
  const [booking, setBooking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!pickup || !drop) { navigate("/majhe/book"); return; }
    setLoading(true);
    passengerApi.get(`/search-buses?pickup=${encodeURIComponent(pickup)}&drop=${encodeURIComponent(drop)}`)
      .then(({ data }) => {
        setBuses(data.buses || []);
        setMultiLeg(data.multi_leg || []);
      })
      .finally(() => setLoading(false));
  }, [pickup, drop, navigate]);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      const { data } = await passengerApi.post("/tickets/buy", {
        route_id: selected.route_id, from_stop: pickup, to_stop: drop, passengers,
      });
      toast.success("Ticket booked!");
      navigate(`/majhe/tickets/${data.ticket_no}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  };

  if (selected) {
    return (
      <BookingConfirm
        bus={selected}
        pickup={pickup}
        drop={drop}
        passengers={passengers}
        onPassengers={setPassengers}
        onBack={() => setSelected(null)}
        onConfirm={book}
        booking={booking}/>
    );
  }

  return (
    <div data-testid="majhe-bus-results">
      <MajheHeader title="Available Buses" onBack={() => navigate("/majhe/book")}/>
      <div className="px-5 pt-1 pb-2">
        <div className="text-[12px] text-[#7B7390]">From → To</div>
        <div className="font-bold text-[15px] text-[#18142A]">{pickup} → {drop}</div>
      </div>

      <div className="px-5 mt-3 space-y-3">
        {loading && [0, 1, 2].map((i) => <div key={i} className="majhe-skel h-32"/>)}

        {!loading && buses.length === 0 && multiLeg.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
              <Bus size={28} weight="bold"/>
            </div>
            <div className="text-[#18142A] font-bold mt-3">No buses found</div>
            <div className="text-[12px] text-[#7B7390] mt-1">Try different pickup/drop names</div>
          </div>
        )}

        {!loading && buses.length > 0 && (
          <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold pt-1" data-testid="direct-buses-header">
            1-Bus Options · {buses.length}
            <span className="ml-1.5 normal-case font-medium text-[#9F95B5] tracking-normal">(no transfer needed)</span>
          </div>
        )}

        {!loading && buses.map((b, idx) => {
          // Extract intermediate "via <STOP>" from the GTFS route name so we can
          // accurately tag the bus instead of mislabelling it as DIRECT when it
          // clearly passes through a major intermediate hub.
          const viaMatch = (b.route_name || "").match(/\s+via\s+(.+?)$/i);
          const viaStop = viaMatch ? viaMatch[1].trim() : null;
          // Clean route name for display (drop the redundant ' via X' suffix —
          // we'll show it as its own pill instead).
          const cleanName = viaStop
            ? (b.route_name || "").replace(/\s+via\s+.+?$/i, "").trim()
            : (b.route_name || "");
          return (
          <button key={`${b.route_id}-${b.trip_id || idx}`} data-testid={`bus-result-${b.route_id}`}
            onClick={() => setSelected(b)}
            className="majhe-bus-card text-left w-full">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="majhe-pill majhe-pill--violet">{b.route_short_name || b.route_id}</span>
              {b.vehicle_type?.includes("EV") && (
                <span className="majhe-pill majhe-pill--green">
                  <Lightning size={11} weight="fill"/> EV
                </span>
              )}
              <span className="majhe-pill majhe-pill--blue">{b.vehicle_type}</span>
              {viaStop ? (
                <span className="majhe-pill majhe-pill--violet" data-testid={`badge-via-${b.route_id}`}>
                  VIA {viaStop}
                </span>
              ) : (
                <span className="majhe-pill majhe-pill--green" data-testid={`badge-direct-${b.route_id}`}>
                  DIRECT
                </span>
              )}
              {b.fare_official && (
                <span className="majhe-pill majhe-pill--gold" data-testid={`fare-official-${b.route_id}`}>
                  <SealCheck size={11} weight="fill"/> Official Fare
                </span>
              )}
            </div>
            <div className="font-extrabold text-[15.5px] text-[#18142A] leading-snug">{cleanName || b.route_name}</div>

            <div className="flex items-center justify-between pt-1 border-t border-dashed border-[#EBE5FB]">
              <div className="flex items-center gap-1.5">
                <Clock size={14} className="text-[#5B2EE5]"/>
                <span className="text-sm font-bold text-[#5B2EE5]">
                  ETA · {b.eta_min} min
                  {b.arrival_time && (
                    <span className="text-[10.5px] font-semibold text-[#7B7390] ml-1.5">
                      ({b.arrival_time.slice(0, 5)})
                    </span>
                  )}
                </span>
              </div>
              <div className="text-[#18142A] font-extrabold flex items-center text-base">
                <CurrencyInr size={15} weight="bold"/>{b.fare}
                <span className="text-[11px] text-[#7B7390] font-semibold ml-1">/seat</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[12px] text-[#5B2EE5] bg-[#F5F2FF] px-3 py-2 rounded-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5B2EE5] animate-pulse"/>
              {b.current_status}
            </div>

            <div className="flex items-center justify-between text-[11.5px] text-[#7B7390]">
              <span>{b.registration_number || b.fleet_id} · {b.occupancy}</span>
              <span className="flex items-center gap-1 font-bold text-[#5B2EE5]">Book <ArrowRight size={12} weight="bold"/></span>
            </div>
          </button>
          );
        })}

        {!loading && multiLeg.length > 0 && (
          <PlanMyJourneySection legs={multiLeg} pickup={pickup} drop={drop}
            hasDirect={buses.length > 0}/>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Plan My Journey — multi-leg suggestions (transfer hub)
============================================================ */
function PlanMyJourneySection({ legs, pickup, drop, hasDirect }) {
  return (
    <div className="pt-3 space-y-3" data-testid="plan-my-journey-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold flex items-center gap-1.5">
            <Path size={12} weight="bold" className="text-[#5B2EE5]"/> Plan My Journey
          </div>
          <div className="text-[12px] text-[#18142A] font-semibold mt-0.5">
            {hasDirect ? "Also consider these multi-leg options" : "No single-bus option — try a transfer"}
          </div>
        </div>
        <span className="majhe-pill majhe-pill--violet">{legs.length} option{legs.length === 1 ? "" : "s"}</span>
      </div>

      {legs.map((leg, idx) => (
        <MultiLegCard key={idx} leg={leg} idx={idx} pickup={pickup} drop={drop}/>
      ))}
    </div>
  );
}

function MultiLegCard({ leg, idx, pickup, drop }) {
  const fmt = (t) => (t ? t.slice(0, 5) : "—");
  return (
    <div className="majhe-multileg-card" data-testid={`multileg-card-${idx}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="majhe-pill majhe-pill--violet">VIA TRANSFER</span>
          {leg.fare_official && (
            <span className="majhe-pill majhe-pill--gold">
              <SealCheck size={11} weight="fill"/> Official Fare
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#7B7390] font-bold">Total</div>
          <div className="text-base font-extrabold text-[#18142A] flex items-center justify-end leading-none">
            <CurrencyInr size={14} weight="bold"/>{leg.total_fare}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mt-2.5">
        <Stat icon={<Hourglass size={12} weight="bold"/>} label="Total time" value={`${leg.total_min} min`}/>
        <Stat icon={<Clock size={12} weight="bold"/>} label="Layover" value={`${leg.layover_min} min`}/>
        <Stat icon={<MapPin size={12} weight="bold"/>} label="Transfer"
          value={leg.transfer_at} truncate/>
      </div>

      {/* Leg 1 */}
      <div className="mt-3 rounded-xl bg-[#F5F2FF] border border-[#E4D8FB] p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="majhe-pill majhe-pill--violet">LEG 1</span>
            <span className="font-bold text-[12.5px] text-[#5B2EE5]">
              {leg.leg1.route_short_name || leg.leg1.route_id}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[#18142A] font-extrabold text-[13px]">
            <CurrencyInr size={11} weight="bold"/>{leg.leg1.fare}
          </div>
        </div>
        <div className="text-[13px] font-bold text-[#18142A] leading-snug">
          {pickup} → {leg.transfer_at}
        </div>
        <div className="text-[11px] text-[#7B7390] flex items-center gap-3">
          <span>Dep {fmt(leg.leg1.depart_time)}</span>
          <span>Arr {fmt(leg.leg1.arrive_time)}</span>
          {leg.leg1.depot && <span className="text-[10px]">· {leg.leg1.depot}</span>}
        </div>
      </div>

      {/* Layover arrow */}
      <div className="flex items-center gap-2 py-1.5 px-3">
        <ArrowDown size={14} weight="bold" className="text-[#7B7390]"/>
        <span className="text-[11px] font-bold text-[#7B7390]">
          Transfer at {leg.transfer_at} · {leg.layover_min} min wait
        </span>
      </div>

      {/* Leg 2 */}
      <div className="rounded-xl bg-[#FDF7E3] border border-[#F4E4A3] p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="majhe-pill" style={{ background: "#FBE6A1", color: "#7A5106" }}>LEG 2</span>
            <span className="font-bold text-[12.5px] text-[#7A5106]">
              {leg.leg2.route_short_name || leg.leg2.route_id}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[#18142A] font-extrabold text-[13px]">
            <CurrencyInr size={11} weight="bold"/>{leg.leg2.fare}
          </div>
        </div>
        <div className="text-[13px] font-bold text-[#18142A] leading-snug">
          {leg.transfer_at} → {drop}
        </div>
        <div className="text-[11px] text-[#7B7390] flex items-center gap-3">
          <span>Dep {fmt(leg.leg2.depart_time)}</span>
          <span>Arr {fmt(leg.leg2.arrive_time)}</span>
          {leg.leg2.depot && <span className="text-[10px]">· {leg.leg2.depot}</span>}
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-[10.5px] text-[#7B7390] pt-1.5">
        <CheckCircle size={12} weight="fill" className="text-emerald-600"/>
        Buy two separate tickets · one per leg at each bus
      </div>
    </div>
  );
}

function Stat({ icon, label, value, truncate }) {
  return (
    <div className="rounded-lg bg-white border border-[#EDE8FA] p-2">
      <div className="text-[9.5px] uppercase tracking-wider text-[#7B7390] font-bold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-[12px] font-extrabold text-[#18142A] mt-0.5 ${truncate ? "truncate" : ""}`}
        title={truncate ? value : undefined}>{value}</div>
    </div>
  );
}

function BookingConfirm({ bus, pickup, drop, passengers, onPassengers, onBack, onConfirm, booking }) {
  const total = bus.fare * passengers;
  return (
    <div data-testid="majhe-book-confirm">
      <MajheHeader title="Confirm Booking" onBack={onBack}/>

      <div className="px-5 pt-2 space-y-4">
        <div className="majhe-card">
          <div className="text-[11px] uppercase tracking-wider opacity-90">Selected Bus</div>
          <div className="font-extrabold text-lg mt-0.5">{bus.route_name}</div>
          <div className="text-sm opacity-85 mt-1">
            {bus.registration_number || bus.fleet_id} · {bus.vehicle_type}
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="majhe-card-btn"><Clock size={12} weight="bold"/> ETA {bus.eta_min} min</span>
            {bus.arrival_time && (
              <span className="majhe-card-btn">Arrives {bus.arrival_time.slice(0, 5)}</span>
            )}
            {bus.fare_official && (
              <span className="majhe-card-btn"><SealCheck size={12} weight="fill"/> Official Fare</span>
            )}
          </div>

          <div className="mt-4 rounded-xl bg-white/10 border border-white/15 p-3">
            <div className="text-[11px] uppercase tracking-wide opacity-90">Current bus status</div>
            <div className="text-sm font-bold mt-0.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse"/>
              {bus.current_status}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#EDE8FA] p-4 space-y-3">
          <div className="grid grid-cols-[28px_1fr] gap-3 items-center">
            <NavigationArrow size={20} weight="fill" className="text-[#5B2EE5]"/>
            <div>
              <div className="text-[11px] text-[#7B7390] uppercase font-semibold">Pickup</div>
              <div className="font-bold text-sm text-[#18142A]">{pickup}</div>
            </div>
          </div>
          <div className="grid grid-cols-[28px_1fr] gap-3 items-center">
            <MapPin size={20} weight="fill" className="text-[#5B2EE5]"/>
            <div>
              <div className="text-[11px] text-[#7B7390] uppercase font-semibold">Drop</div>
              <div className="font-bold text-sm text-[#18142A]">{drop}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#EDE8FA] p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] uppercase tracking-wider text-[#7B7390] font-semibold flex items-center gap-1.5">
              <Users size={14}/> Passengers
            </span>
            <span className="text-[14px] font-extrabold text-[#18142A]">{passengers}</span>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => onPassengers(n)} data-testid={`pax-${n}`}
                className={`py-2 rounded-lg text-sm font-bold transition-colors ${
                  passengers === n ? "bg-[#5B2EE5] text-white" : "bg-[#F5F2FF] text-[#5B2EE5]"
                }`}>
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#EDE8FA] p-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-semibold">Total to pay</div>
            <div className="text-2xl font-extrabold text-[#18142A] flex items-center mt-0.5">
              <CurrencyInr size={20} weight="bold"/>{total.toLocaleString()}
            </div>
          </div>
          <div className="text-right text-[11px] text-[#7B7390]">
            <div>{passengers} × ₹{bus.fare}</div>
            <div className="mt-0.5">Demo · No real charge</div>
          </div>
        </div>

        <button data-testid="confirm-book-btn" onClick={onConfirm} disabled={booking}
          type="button"
          aria-label={booking ? "Booking your ticket, please wait" : `Confirm booking and generate QR ticket, total ${total} rupees`}
          className="majhe-btn">
          {booking ? "Booking…" : "Confirm & Generate QR"}
        </button>
      </div>
    </div>
  );
}
